import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { TitleDetail as TitleDetailModel } from "@/api/types";
import { Row } from "@/components/Row";
import { Spinner } from "@/components/Spinner";
import { TitleCard } from "@/components/TitleCard";
import { useResource } from "@/hooks/useResource";

function runtime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function TitleDetail() {
  const { titleId = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useResource<TitleDetailModel>(
    titleId ? `/api/v1/catalog/titles/${titleId}` : null,
  );
  const [season, setSeason] = useState(1);
  const [explanation, setExplanation] = useState<string | null>(null);

  if (loading) return <Spinner />;
  if (error || !data) {
    return (
      <main className="page container">
        <div className="alert alert--error" style={{ marginTop: 40 }}>
          {error?.message ?? "That title is not available."}
        </div>
      </main>
    );
  }

  // Narrowed once so the async handlers below keep the non-null type.
  const title = data;
  const resumeAt = title.progress?.position_seconds ?? 0;
  const currentSeason = title.seasons.find((item) => item.number === season) ?? title.seasons[0];

  async function toggleWatchlist() {
    await api.post("/api/v1/playback/watchlist", { title_id: title.id });
    reload();
  }

  async function react(reaction: "up" | "down" | "love") {
    if (title.reaction === reaction) await api.delete(`/api/v1/playback/titles/${title.id}/react`);
    else await api.post(`/api/v1/playback/titles/${title.id}/react`, { reaction });
    reload();
  }

  async function explain() {
    const payload = await api.get<{ explanation: string }>(
      `/api/v1/discovery/recommendations/${title.id}/explain`,
    );
    setExplanation(payload.explanation);
  }

  return (
    <main className="page page--flush">
      <div
        className="detail__backdrop"
        style={{
          background: data.dominant_color,
          backgroundImage: data.backdrop_url ? `url(${data.backdrop_url})` : undefined,
        }}
      >
        <div className="hero__scrim" />
      </div>

      <div className="container detail__body">
        <div>
          <h1 style={{ fontSize: "clamp(32px, 5vw, 56px)" }}>{data.name}</h1>
          {data.tagline && (
            <p style={{ color: "var(--accent-bright)", marginTop: 6 }}>{data.tagline}</p>
          )}
          <div className="hero__meta" style={{ marginTop: 14 }}>
            <span className="chip">{data.maturity_rating}</span>
            {data.release_year && <span>{data.release_year}</span>}
            <span>{data.kind === "movie" ? runtime(data.runtime_seconds) : `${data.seasons.length} seasons`}</span>
            <span>{data.genres.join(" · ")}</span>
            {data.average_rating > 0 && <span>{data.average_rating.toFixed(1)} / 5</span>}
          </div>

          <div className="hero__actions" style={{ marginBottom: 22 }}>
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => navigate(`/watch/${data.id}`)}
            >
              {resumeAt > 0 ? `Resume from ${Math.floor(resumeAt / 60)}m` : "Play"}
            </button>
            <button className="btn btn--ghost" type="button" onClick={() => void toggleWatchlist()}>
              {data.on_watchlist ? "In my list" : "Add to my list"}
            </button>
            <button
              className={data.reaction === "up" ? "btn btn--accent" : "btn btn--ghost"}
              type="button"
              onClick={() => void react("up")}
            >
              Like
            </button>
            <button
              className={data.reaction === "love" ? "btn btn--accent" : "btn btn--ghost"}
              type="button"
              onClick={() => void react("love")}
            >
              Love
            </button>
            <button className="btn btn--quiet" type="button" onClick={() => void explain()}>
              Why this?
            </button>
          </div>

          {explanation && <div className="alert alert--info">{explanation}</div>}

          <p style={{ fontSize: 16, color: "var(--paper-200)", maxWidth: "62ch" }}>
            {data.synopsis}
          </p>

          {data.content_warnings.length > 0 && (
            <p style={{ color: "var(--warning)", fontSize: 13 }}>
              Contains: {data.content_warnings.join(", ")}
            </p>
          )}

          {data.kind === "series" && currentSeason && (
            <section style={{ marginTop: 34 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h2 style={{ fontSize: 20 }}>Episodes</h2>
                {data.seasons.length > 1 && (
                  <select
                    value={season}
                    onChange={(event) => setSeason(Number(event.target.value))}
                    style={{
                      background: "var(--ink-200)",
                      border: "1px solid var(--ink-400)",
                      borderRadius: 8,
                      padding: "6px 10px",
                    }}
                  >
                    {data.seasons.map((item) => (
                      <option key={item.number} value={item.number}>
                        {item.display_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                {currentSeason.episodes.map((episode) => (
                  <Link
                    key={episode.id}
                    to={`/watch/${data.id}?episode=${episode.id}`}
                    className="episode"
                  >
                    <div className="episode__still">
                      {episode.still_url && <img src={episode.still_url} alt="" loading="lazy" />}
                    </div>
                    <div>
                      <strong>
                        {episode.number}. {episode.name}
                      </strong>
                      <div style={{ color: "var(--paper-400)", fontSize: 12, margin: "2px 0 6px" }}>
                        {runtime(episode.runtime_seconds)}
                      </div>
                      <p style={{ margin: 0, color: "var(--paper-300)", fontSize: 13.5 }}>
                        {episode.synopsis}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside>
          <dl className="detail__facts">
            {data.cast.length > 0 && (
              <>
                <dt>Cast</dt>
                <dd>{data.cast.map((person) => person.name).join(", ")}</dd>
              </>
            )}
            {data.crew.length > 0 && (
              <>
                <dt>Crew</dt>
                <dd>
                  {data.crew.map((person) => `${person.name} (${person.role})`).join(", ")}
                </dd>
              </>
            )}
            {data.themes.length > 0 && (
              <>
                <dt>Themes</dt>
                <dd>{data.themes.join(", ")}</dd>
              </>
            )}
            {data.moods.length > 0 && (
              <>
                <dt>Mood</dt>
                <dd>{data.moods.join(", ")}</dd>
              </>
            )}
            {data.audience_segments.length > 0 && (
              <>
                <dt>For viewers who like</dt>
                <dd>{data.audience_segments.join(", ")}</dd>
              </>
            )}
          </dl>
        </aside>
      </div>

      {data.similar && data.similar.length > 0 && (
        <Row title="More like this">
          {data.similar.map((item) => (
            <TitleCard key={item.id} title={item} />
          ))}
        </Row>
      )}
    </main>
  );
}
